import classNames from 'classnames';
import React, { useState } from 'react';
import { useCreateReceiptPdf } from '../../../hooks/apolloHooks/useCreateReceiptPdf';
import type { Transaction } from '../../../integrations/allkort/graphql/interface/Customer';
import { DownloadIcon } from '../../svgs/ExpressonSVGs';


type TransactionTableSingleRowProps = {
    transaction: Transaction;
    maskedPan?: string;
    productName?: string;
    isCompany?: boolean;
    siteName?: string;
    cardRef?: string | null | undefined;
    isNewlyLoaded?: boolean;
    isCardDisabled?: boolean;
}

const TransactionTableSingleRow: React.FC<TransactionTableSingleRowProps> = ({ transaction, maskedPan, productName, isCompany = false, siteName, cardRef, isNewlyLoaded = false, isCardDisabled = false }) => {

    // TODO: You will render transaction data here
    // Available fields on transaction:
    // - transaction.at (date string)
    // - transaction.amount
    // - transaction.price
    // - transaction.quantity
    // - transaction.cardNumber
    // - transaction.transactionType ('B', 'K', 'P')
    // - transaction.productId
    // - transaction.siteId
    // - transaction.customerId
    const formattedDate = new Date(transaction.at).toLocaleDateString('sv-SE');
    const [toggleOpen, setToggleOpen] = useState(false);
    const [receiptUrl, setReceiptUrl] = useState<string>('');

    // Skip transactions with 0 amount
    if (transaction.amount === 0) {
        return null;
    }

    // Get receipt PDF link
    const { createReceiptPdf, loading: pdfLoading } = useCreateReceiptPdf();

    // Handle download button click - fetch PDF and open in new tab
    const handleDownloadClick = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('receipt url', receiptUrl);

        // If we already have the URL, open it
        if (receiptUrl) {
            window.open(receiptUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        //console.log('transaction id', transaction.id);
        // Fetch the PDF URL
        try {
            const result = await createReceiptPdf(transaction.id);

            // Check if result is null or if there's no URL (indicates an error occurred)
            if (!result || !result.url) {
                console.error('Failed to fetch receipt PDF: No URL returned');
                return;
            }

            setReceiptUrl(result.url);
            // Open the URL immediately after fetching
            window.open(result.url, '_blank', 'noopener,noreferrer');
        } catch (err) {
            console.error('Failed to fetch receipt PDF URL:', err);
        }
    };

    // Return card type Swedish word
    const getCardTypeText = () => {
        if (transaction.transactionType === 'P') return 'Smartkort'; // Smartcard
        if (transaction.transactionType === 'K') return isCompany ? 'Företagskort' : 'Tankkort'; // Företagskort or Tankkort
        if (transaction.transactionType === 'B') return 'Bankkort'; // Bankcard
        return '';
    };

    return (
        <div
            className={classNames(
                'transaction-table-single-row', {
                'type-smartcard': transaction.transactionType === 'P',
                'type-creditcard': transaction.transactionType === 'K',
                'type-bankcard': transaction.transactionType === 'B',
                'is-company': isCompany,
                'open': toggleOpen,
                'newly-loaded': isNewlyLoaded,
            }
            )}
        >
            {/* Your task: Display the transaction data here */}

            <div
                className="transaction-table-single-row__header"
                role="button"
                tabIndex={0}
                onClick={() => setToggleOpen(!toggleOpen)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setToggleOpen(!toggleOpen);
                    }
                }}
            >
                <span className="transaction-date" data-label="Datum" aria-label={`Datum: ${formattedDate}`}>{formattedDate}</span>
                {/* TODO: check bankcard against card id so it does not show the wrong card number.  */}
                <span
                    className="transaction-masked-card-number"
                    data-label="Kortnr"
                    aria-label={`Korttyp: ${getCardTypeText()}, Kortnummer: ${maskedPan || 'Bankkort'}${isCardDisabled ? ', spärrad' : ''}`}
                >
                    <>
                        <span>
                            {getCardTypeText().charAt(0)}<span className="expanding-right">{getCardTypeText().slice(1)}</span>
                        </span>
                        {maskedPan || 'Bankkort'}
                        {isCardDisabled && <> (spärrad)</>}
                    </>
                </span>
                <span className="transaction-site" data-label="Station" aria-label={`Station: ${siteName}`}>
                    {siteName}
                </span>
                <span className="transaction-product-type" data-label="Produkt" aria-label={`Produkt: ${productName || transaction.productId}`}>{productName || transaction.productId}</span>
                <span className="transaction-amount" data-label="Belopp" aria-label={`Belopp: ${transaction.amount.toLocaleString('sv-SE', { maximumFractionDigits: 2 })} kr`}>{transaction.amount.toLocaleString('sv-SE', { maximumFractionDigits: 2 })} kr</span>

            </div>
            <div className="transaction-table-single-row__details">
                {toggleOpen && (
                    <>
                        {transaction.transactionType !== 'B' && (
                            <div className="card-reference" aria-label={`Kortreferens: ${cardRef || 'Saknas'}`}>
                                <label>Kortnamn: </label>
                                <span>{cardRef || 'Saknas'}</span>
                            </div>
                        )}

                        <div className="transaction-time" aria-label={`Transaktionstid: ${new Date(transaction.at).toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}`}>
                            <label>Transaktionstid: </label>
                            <span>{new Date(transaction.at).toLocaleString('sv-SE', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                            })}</span>
                        </div>

                        <div className="volume-price" aria-label={`Literpris: ${transaction.price.toFixed(2)} kr`}>
                            <label>Literpris: </label>
                            <span>
                                {transaction.price.toLocaleString('sv-SE', { maximumFractionDigits: 2 })} kr
                            </span>
                        </div>

                        <div className="volume" aria-label={`Volym: ${transaction.quantity.toLocaleString('sv-SE', { maximumFractionDigits: 2 })} liter`}>
                            <label>Volym: </label>
                            <span>
                                {transaction.quantity.toLocaleString('sv-SE', { maximumFractionDigits: 2 })} liter
                            </span>
                        </div>

                        {receiptUrl && (
                            <div className="download-receipt">
                                <a className="download-button body-text" href={receiptUrl} target="_blank" rel="noopener noreferrer">
                                    <DownloadIcon width={18} height={18} />
                                    Visa kvitto
                                </a>
                            </div>
                        )}
                        {pdfLoading && !receiptUrl && (
                            <div className="download-receipt">
                                <span className="loading-receipt body-text">
                                    <img
                                        src="/static/images/icons-animated/explode-loading-animated.webp"
                                        alt="Loading"
                                        className=""
                                        width={32}
                                        height={32}
                                    />
                                    Laddar kvitto...
                                </span>
                            </div>
                        )}
                        {!receiptUrl && !pdfLoading && (
                            <div className="download-receipt">
                                <button className="download-button body-text" onClick={handleDownloadClick} type="button">
                                    <DownloadIcon width={18} height={18} />Visa kvitto
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

        </div>
    )
}

export default TransactionTableSingleRow
